import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';

const PositionHistoryModal   = lazy(() => import('../components/PositionHistoryModal'));
const PositionHistoryOverlay = lazy(() => import('../components/PositionHistoryOverlay'));
const WinnerCelebration      = lazy(() => import('../components/WinnerCelebration'));

function MovementBadge({ delta }) {
  if (delta === null || delta === undefined) return null;
  if (delta > 0) {
    return <span style={{ color: 'var(--good)', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)' }}>↑{delta}</span>;
  }
  if (delta < 0) {
    return <span style={{ color: 'var(--danger)', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)' }}>↓{Math.abs(delta)}</span>;
  }
  return <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)' }}>=</span>;
}

export default function Leaderboard() {
  const { user } = useAuth();
  const api = useApi();

  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [board, setBoard] = useState([]);
  const [leagueLoading, setLeagueLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [historyTarget, setHistoryTarget] = useState(null); // { userId, name }
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [tournamentOver, setTournamentOver] = useState(false);
  const [celebrate, setCelebrate] = useState(false);

  // Tournament is "over" once the Final (M104) has a full-time result.
  useEffect(() => {
    api.getResults()
      .then(rs => { const f = rs.find(r => r.matchId === 'M104'); setTournamentOver(!!f && f.status === 'FT'); })
      .catch(() => {});
  }, []); // eslint-disable-line

  // Auto-play the league-winner celebration once per league when the tournament ends.
  const leagueWinner = board[0];
  useEffect(() => {
    if (!tournamentOver || !selectedLeague || !leagueWinner) return;
    const key = `wc2026_celebrated_league_${selectedLeague}`;
    if (!localStorage.getItem(key)) {
      setCelebrate(true);
      localStorage.setItem(key, '1');
    }
  }, [tournamentOver, selectedLeague, leagueWinner]); // eslint-disable-line

  // Load user's leagues on mount, auto-select from URL or if single league
  useEffect(() => {
    api.getLeagues()
      .then(({ leagues: ls }) => {
        setLeagues(ls);
        const urlLeague = new URLSearchParams(window.location.search).get('league');
        if (urlLeague && ls.find(l => l.leagueId === urlLeague)) {
          setSelectedLeague(urlLeague);
        } else if (ls.length >= 1) {
          setSelectedLeague(ls[0].leagueId);
        }
      })
      .catch(err => setError(err.message))
      .finally(() => setLeagueLoading(false));
  }, []); // eslint-disable-line

  // Fetch leaderboard when selected league changes
  useEffect(() => {
    if (!selectedLeague) return;

    const url = new URL(window.location);
    url.searchParams.set('league', selectedLeague);
    window.history.replaceState({}, '', url);

    setLoading(true);
    setError('');
    api.getLeaderboard(selectedLeague)
      .then(data => { setBoard(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [selectedLeague]); // eslint-disable-line

  if (leagueLoading) return <div className="loading-screen"><div className="spinner" /></div>;

  // No leagues
  if (leagues.length === 0) {
    const isAdmin = user?.groups?.includes('admin');
    return (
      <div className="page">
        <h1 className="page-title">Leaderboard</h1>
        <div className="card" style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--text-muted)' }}>
          {isAdmin
            ? 'No leagues exist yet. Create one in the Admin panel to get started.'
            : "You haven't been added to a league yet. Contact an admin."}
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
        <h1 className="page-title">Leaderboard</h1>
        <div style={{ display: 'flex', gap: '0.4rem' }}>
        {tournamentOver && leagueWinner && (
          <button
            onClick={() => setCelebrate(true)}
            title="Replay the league winner celebration"
            style={{ background: 'transparent', border: '1px solid var(--volt-line)', color: 'var(--volt-bright)', borderRadius: 4, padding: '0.4rem 0.6rem', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)' }}
          >
            🏆 Replay
          </button>
        )}
        <button
          onClick={() => setOverlayOpen(true)}
          title="View position history"
          aria-label="View position history"
          style={{ background: 'transparent', border: '1px solid var(--border-faint)', color: 'var(--text-muted)', borderRadius: 4, padding: '0.4rem 0.6rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)' }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
          </svg>
          History
        </button>
        </div>
      </div>
      <div style={{ marginBottom: '1rem' }}>
        <p className="page-subtitle" style={{ margin: '0 0 0.75rem' }}>Maximum possible score: 226 points</p>
        {leagues.length > 1 && (
          <div className="chip-strip chip-strip--wrap">
            {leagues.map(l => (
              <button
                key={l.leagueId}
                className={`chip${selectedLeague === l.leagueId ? ' active' : ''}`}
                onClick={() => setSelectedLeague(l.leagueId)}
              >
                {l.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {error && <div className="error-msg" style={{ marginBottom: '1rem' }}>{error}</div>}

      {loading ? (
        <div className="loading-screen"><div className="spinner" /></div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
          <table className="leaderboard-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th style={{ width: 36 }} aria-label="Movement" />
                <th>Player</th>
                <th>Winner pick</th>
                <th style={{ textAlign: 'right', width: 36 }} title="Games played (matches with results)">GP</th>
                <th style={{ textAlign: 'right', width: 40 }} title="Predictions made (your real picks — excludes default score)">Pred</th>
                <th style={{ textAlign: 'right', width: 36 }} title="Exact-score predictions (2 points)">2×</th>
                <th style={{ textAlign: 'right', width: 40 }} title="Accurate — correct outcome (1 or 2 pts)">Acc</th>
                <th style={{ textAlign: 'right', width: 40 }} title="Inaccurate — wrong result (0 pts)">Miss</th>
                <th style={{ textAlign: 'right', width: 40 }} title="Matches scored from your default score">Def</th>
                <th style={{ textAlign: 'right', width: 70 }} title="Jokers remaining (out of 4)">Jokers Remaining</th>
                <th style={{ textAlign: 'right' }}>Points</th>
              </tr>
            </thead>
            <tbody>
              {board.length === 0 ? (
                <tr>
                  <td colSpan={12} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '2rem' }}>
                    No scores yet — get predicting!
                  </td>
                </tr>
              ) : (
                board.map(entry => {
                  const rank = entry.rank ?? (board.findIndex(e => e.totalPoints === entry.totalPoints) + 1);
                  const jokersLeft = Math.max(0, 4 - (entry.jokersUsed ?? 0));
                  return (
                    <tr
                      key={entry.userId}
                      className={entry.userId === user.sub ? 'me' : ''}
                      onClick={() => setHistoryTarget({ userId: entry.userId, name: entry.name })}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setHistoryTarget({ userId: entry.userId, name: entry.name }); } }}
                      role="button"
                      tabIndex={0}
                      style={{ cursor: 'pointer' }}
                      title="Tap to see position history"
                    >
                      <td className={`rank ${rank <= 3 ? `rank-${rank}` : ''}`}>{rank}</td>
                      <td><MovementBadge delta={entry.rankDelta} /></td>
                      <td>
                        <span style={{ fontWeight: 500 }}>{entry.name}</span>
                        {entry.userId === user.sub && (
                          <span style={{
                            marginLeft: '0.5rem',
                            fontSize: 'var(--t-2xs)',
                            color: 'var(--spark-bright)',
                            background: 'var(--spark-dim)',
                            border: '1px solid var(--spark-line)',
                            padding: '0.15rem 0.4rem',
                            borderRadius: '4px',
                            fontFamily: 'var(--font-mono)',
                            letterSpacing: '.04em',
                          }}>
                            you
                          </span>
                        )}
                      </td>
                      <td style={{ color: 'var(--text-muted)', fontSize: '0.88rem' }}>{entry.winner || '—'}</td>
                      <td style={{ textAlign: 'right', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: '0.88rem' }}>{entry.matchesScored ?? 0}</td>
                      <td style={{ textAlign: 'right', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: '0.88rem' }}>{entry.predictionsMade ?? 0}</td>
                      <td style={{ textAlign: 'right', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: '0.88rem' }}>{entry.exactCount ?? 0}</td>
                      <td style={{ textAlign: 'right', color: 'var(--volt-deep)', fontFamily: 'var(--font-mono)', fontSize: '0.88rem' }}>{entry.correctCount ?? 0}</td>
                      <td style={{ textAlign: 'right', color: 'var(--text-faint)', fontFamily: 'var(--font-mono)', fontSize: '0.88rem' }}>{entry.incorrectCount ?? 0}</td>
                      <td style={{ textAlign: 'right', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: '0.88rem' }}>{entry.defaultUsedCount ?? 0}</td>
                      <td style={{ textAlign: 'right', color: jokersLeft === 0 ? 'var(--text-faint)' : 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: '0.88rem' }}>{jokersLeft}</td>
                      <td className="points-total">{entry.totalPoints}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}

      <div className="card" style={{ marginTop: '1.5rem' }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)', letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
          Scoring Reference
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
          {[
            { pts: '2 pts', desc: 'Exact score + correct outcome' },
            { pts: '1 pt',  desc: 'Correct outcome only (W/D/L)' },
            { pts: '×2',    desc: 'Joker doubles your match points' },
            { pts: '10 pts', desc: 'Tournament winner (original pick)' },
            { pts: '5 pts',  desc: 'Tournament winner (changed after M1, before R32)' },
          ].map(item => (
            <div key={item.pts} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '1.1rem', color: 'var(--volt)', minWidth: 50 }}>{item.pts}</span>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem', lineHeight: 1.4 }}>{item.desc}</span>
            </div>
          ))}
        </div>
        <p style={{ color: 'var(--text-faint)', fontSize: 'var(--t-xs)', fontFamily: 'var(--font-mono)', marginTop: '0.75rem' }}>
          All points based on 90-minute result only (no extra time or penalties)
        </p>
      </div>

      {historyTarget && (
        <Suspense fallback={null}>
          <PositionHistoryModal
            userId={historyTarget.userId}
            userName={historyTarget.name}
            leagueId={selectedLeague}
            onClose={() => setHistoryTarget(null)}
          />
        </Suspense>
      )}
      {overlayOpen && (
        <Suspense fallback={null}>
          <PositionHistoryOverlay
            leagueId={selectedLeague}
            currentUserId={user.sub}
            onClose={() => setOverlayOpen(false)}
          />
        </Suspense>
      )}

      {celebrate && leagueWinner && (
        <Suspense fallback={null}>
          <WinnerCelebration kind="league" name={leagueWinner.name} onClose={() => setCelebrate(false)} />
        </Suspense>
      )}
    </div>
  );
}
